import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectAnswer } from "../../store/selectors/answerSelectors";
import { selectQuestionMap, selectQuestionLocationMap, selectChecklistLoading } from "../../store/selectors/checklistSelectors";
import { useCallback, useEffect, useMemo } from "react";
import { setFailed, setFilteredAnswers, setFilters, setPage, updateItem } from "../../store/slices/answerSlice";
import { defaultAnswerColumns } from "../../utils/answerTableHelper";
import { AnswerTableHeader } from "./AnswerTableHeader";
import { cloneDeep } from "lodash";
import { ColumnEvent } from "primereact/column";
import { updateAnswer } from "../../services/answerService";
import { Answer } from "../../types/answer";
import { fetchChecklist } from "../../store/thunks/checklistThunks";
import { fetchAttachments } from "../../store/thunks/attachmentThunks";
import { AttachmentGalleria } from "./AttachmentGalleria";

export const AnswerTable = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { data, loading, globalFilterValue, filters, page, selectedInspection } = useAppSelector(selectAnswer);
    const questionMap = useAppSelector(selectQuestionMap);
    const questionLocationMap = useAppSelector(selectQuestionLocationMap);
    const checklistLoading = useAppSelector(selectChecklistLoading);

    // Fetch checklist config and attachments when inspection changes
    useEffect(() => {
        if (selectedInspection) {
            const inspection = selectedInspection as Record<string, unknown>;
            const checklistType = inspection.checklistType as string;
            const checklistVersion = inspection.checklistVersion as string;
            const inspectionId = inspection.inspectionId as string;

            if (checklistType) {
                dispatch(fetchChecklist(checklistType, checklistVersion || "latest"));
            }

            if (inspectionId) {
                dispatch(fetchAttachments(inspectionId));
            }
        }
    }, [dispatch, selectedInspection]);

    const onPage = useCallback((event: DataTableStateEvent) => {
        dispatch(setPage({ row: event.rows, first: event.first }));
    }, [dispatch]);

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFilters(event.filters));
    }, [dispatch]);

    const onValueChange = useCallback((value: Answer[]) => {
        dispatch(setFilteredAnswers(value));
    }, [dispatch]);

    const onCellEditComplete = useCallback(async (event: ColumnEvent) => {
        const { newValue, newRowData, value } = event;

        if (newValue !== value) {
            dispatch(updateItem(newRowData));

            const updated = await updateAnswer(newRowData);

            if (!updated) {
                dispatch(setFailed([newRowData.answerId]));
            }
        }
    }, [dispatch]);

    const renderHeader = useMemo(
        () => <AnswerTableHeader />,
        [globalFilterValue]
    );

    const columns = useMemo(() => defaultAnswerColumns(onCellEditComplete, t, questionMap, questionLocationMap), [onCellEditComplete, t, questionMap, questionLocationMap]);
    const rowOptions = useMemo(() => [50, 75, 100, 150, 250], []);
    const isLoading = loading || checklistLoading;
    const globalFilterFields = useMemo(() => ['answerId', 'questionText', 'value', 'dataDictionaryId', 'questionType', 'sectionTitle', 'subsectionTitle'], []);

    // Enrich data with section/subsection titles for filtering
    const enrichedData = useMemo(() => {
        return cloneDeep(data).map(answer => {
            const location = questionLocationMap[answer.questionId];
            return {
                ...answer,
                sectionTitle: location?.sectionTitle || "",
                subsectionTitle: location?.subsectionTitle || "",
            };
        });
    }, [data, questionLocationMap]);

    const sortedData = useMemo(() => {
        return [...enrichedData].sort((a, b) => {
            const questionCompare = a.questionId.localeCompare(b.questionId);
            if (questionCompare !== 0) return questionCompare;
            return a.answerId.localeCompare(b.answerId);
        });
    }, [enrichedData]);

    return (
        <>
            <DataTable
                value={sortedData}
                dataKey="answerId"
                loading={isLoading}
                filters={filters}
                globalFilterFields={globalFilterFields}
                onFilter={onFilter}
                onValueChange={onValueChange}
                filterDisplay="row"
                editMode="cell"
                paginator
                scrollable
                removableSort
                stripedRows
                size="small"
                first={page.first}
                rows={page.row}
                rowsPerPageOptions={rowOptions}
                onPage={onPage}
                header={renderHeader}
                rowGroupMode="rowspan"
                groupRowsBy="questionId"
                sortField="questionId"
                sortOrder={1}
            >
                {columns}
            </DataTable>
            <AttachmentGalleria />
        </>
    );
};
